/**
 * Pricing Rule Form — group builder UI
 * Trigger and Expected both use AND-of-OR-groups: (A or B) AND (C or D)
 */

import React, { useMemo } from 'react';
import { Button, Checkbox, Divider, Form, Input, InputNumber, Select, Space, Tag } from 'antd';
import { IConfigOption } from '../../../types/configOption';
import { IProduct } from '../../../types/productSetup';
import { PricingConditionOperator } from '../../../types/pricingSetup';

const OPERATOR_OPTIONS: { label: string; value: PricingConditionOperator }[] = [
  { label: 'Greater than (>)', value: '>' },
  { label: 'Less than (<)', value: '<' },
  { label: 'Greater than or equal (>=)', value: '>=' },
  { label: 'Less than or equal (<=)', value: '<=' },
  { label: 'Equal (=)', value: '=' },
  { label: 'Exists (is selected)', value: 'exists' },
  { label: 'Differs (trigger ≠ expected)', value: 'differs' },
];

interface PricingRuleFormProps {
  categories?: Array<{ value: string; label: string; categoryType?: number }>;
  allConfigOptions?: IConfigOption[];
  products?: IProduct[];
}

export const PricingRuleForm: React.FC<PricingRuleFormProps> = ({
  allConfigOptions = [],
  products = [],
}) => {
  const form = Form.useFormInstance();
  const conditionOperator = Form.useWatch('dogma_condition_operator', form);
  const showConditionValue = conditionOperator && conditionOperator !== 'exists' && conditionOperator !== 'differs';

  // Combined options list for both trigger and expected selects
  const allOptions = useMemo(() => {
    const configItems = allConfigOptions.map(opt => ({
      value: opt.dogma_configurationoptionsid,
      label: `${opt['dogma_optiontype@OData.Community.Display.V1.FormattedValue'] || ''} - ${opt.dogma_name}`,
      name: opt.dogma_name,
      optionTypeName: opt['dogma_optiontype@OData.Community.Display.V1.FormattedValue'] || '',
    }));
    const productItems = products.map(p => ({
      value: p.productid,
      label: `Variation - ${p.name}`,
      name: p.name,
      optionTypeName: 'Variation',
    }));
    return [...configItems, ...productItems];
  }, [allConfigOptions, products]);

  const renderGroupSelect = (fieldName: number, index: number, color: 'blue' | 'purple') => (
    <Form.Item
      name={[fieldName]}
      className="flex-1 mb-2"
      rules={[{ required: true, message: 'Select at least one option for this group' }]}
    >
      <Select
        mode="multiple"
        placeholder={`Group ${index + 1}: select options (any one satisfies this group)`}
        showSearch
        filterOption={(input, option) =>
          String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
        }
      >
        {allOptions.map(opt => (
          <Select.Option key={opt.value} value={opt.value} label={opt.label}>
            <Space size={4}>
              {opt.optionTypeName && (
                <Tag color={color} style={{ fontSize: 11, padding: '0 4px', lineHeight: '16px' }}>
                  {opt.optionTypeName}
                </Tag>
              )}
              <span>{opt.name}</span>
            </Space>
          </Select.Option>
        ))}
      </Select>
    </Form.Item>
  );

  return (
    <div className="pt-4">
      <Form.Item
        label="Rule Name"
        name="dogma_name"
        rules={[{ required: true, message: 'Please enter rule name' }]}
      >
        <Input placeholder="e.g., Extra charge for large height" />
      </Form.Item>

      {/* ── Trigger ─────────────────────────────────────────────────────────── */}
      <Divider titlePlacement="left" className="my-4 text-sm font-semibold">
        Trigger — When to apply this rule
      </Divider>
      <p className="text-xs text-gray-500 mb-3">
        Each group = OR (any option in group matches). Multiple groups = AND (all groups must pass).
        Example: <span className="font-mono">(White or Cream) AND (Front spray or Back spray)</span>
      </p>

      <Form.List
        name="dogma_trigger_groups"
        rules={[{
          validator: async (_, groups) => {
            if (!groups?.length || (groups as string[][]).every(g => !g?.length)) {
              return Promise.reject('Add at least one trigger condition group with options');
            }
          },
        }]}
      >
        {(fields, { add, remove }, { errors }) => (
          <>
            {fields.map((field, index) => (
              <React.Fragment key={field.key}>
                {index > 0 && (
                  <div className="flex items-center my-2">
                    <div className="flex-1 border-t border-dashed border-orange-300" />
                    <span className="mx-3 text-xs font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded">
                      AND
                    </span>
                    <div className="flex-1 border-t border-dashed border-orange-300" />
                  </div>
                )}
                <div className="flex gap-2 items-start">
                  {renderGroupSelect(field.name, index, 'blue')}
                  {fields.length > 1 && (
                    <Button
                      size="small"
                      danger
                      onClick={() => remove(field.name)}
                      className="mt-1 flex-shrink-0"
                    >
                      ✕
                    </Button>
                  )}
                </div>
              </React.Fragment>
            ))}
            <Button type="dashed" onClick={() => add([])} className="w-full mb-1">
              + Add AND Condition (new group)
            </Button>
            <Form.ErrorList errors={errors} />
          </>
        )}
      </Form.List>

      {/* ── Condition ───────────────────────────────────────────────────────── */}
      <Divider titlePlacement="left" className="my-4 text-sm font-semibold">
        Condition — How to check (Optional)
      </Divider>

      <div className="grid grid-cols-2 gap-4">
        <Form.Item
          label="Condition Operator"
          name="dogma_condition_operator"
          tooltip="Optional: Add a condition to check (e.g., > 1300, = 'Yes')"
        >
          <Select
            placeholder="Select operator (optional)"
            allowClear
            options={OPERATOR_OPTIONS}
          />
        </Form.Item>

        {showConditionValue && (
          <Form.Item
            label="Condition Value"
            name="dogma_condition_value"
            rules={[{ required: showConditionValue, message: 'Please enter condition value' }]}
            tooltip="The value to compare against (e.g., 2400, 'Yes', 'Both')"
          >
            <Input placeholder="e.g., 2400 or 'Yes'" />
          </Form.Item>
        )}
      </div>

      {/* ── Expected ────────────────────────────────────────────────────────── */}
      <Divider titlePlacement="left" className="my-4 text-sm font-semibold">
        Expected — What gets affected (Optional)
      </Divider>
      <p className="text-xs text-gray-500 mb-3">
        Optional. Same AND-of-OR-group logic as Trigger. Leave empty for value-based rules.
      </p>

      <Form.List name="dogma_expected_groups">
        {(fields, { add, remove }) => (
          <>
            {fields.map((field, index) => (
              <React.Fragment key={field.key}>
                {index > 0 && (
                  <div className="flex items-center my-2">
                    <div className="flex-1 border-t border-dashed border-purple-300" />
                    <span className="mx-3 text-xs font-bold text-purple-600 bg-purple-50 px-2 py-0.5 rounded">
                      AND
                    </span>
                    <div className="flex-1 border-t border-dashed border-purple-300" />
                  </div>
                )}
                <div className="flex gap-2 items-start">
                  {renderGroupSelect(field.name, index, 'purple')}
                  <Button
                    size="small"
                    danger
                    onClick={() => remove(field.name)}
                    className="mt-1 flex-shrink-0"
                  >
                    ✕
                  </Button>
                </div>
              </React.Fragment>
            ))}
            <Button type="dashed" onClick={() => add([])} className="w-full mb-1">
              + Add AND Condition (new group)
            </Button>
          </>
        )}
      </Form.List>

      {/* ── Action ──────────────────────────────────────────────────────────── */}
      <Divider titlePlacement="left" className="my-4 text-sm font-semibold">
        Action — How to apply the price
      </Divider>

      <div className="grid grid-cols-2 gap-4">
        <Form.Item
          label="Price Value (£)"
          name="dogma_price_value"
          rules={[{ required: true, message: 'Please enter price value' }]}
          tooltip="The price amount to add"
        >
          <InputNumber
            placeholder="e.g., 35"
            style={{ width: '100%' }}
            prefix="£"
            min={0}
            step={0.01}
          />
        </Form.Item>

        <div className="space-y-2">
          <Form.Item
            label="Apply Per Frame"
            name="dogma_apply_per_unit"
            valuePropName="checked"
            tooltip="Apply this price once per window frame"
            className="mb-1"
          >
            <Checkbox>Per frame</Checkbox>
          </Form.Item>

          <Form.Item
            label="Apply Per Sash"
            name="dogma_apply_per_sash"
            valuePropName="checked"
            tooltip="Apply this price once per sash (e.g., 2 sashes per frame = price × 2)"
            className="mb-1"
          >
            <Checkbox>Per sash</Checkbox>
          </Form.Item>
        </div>
      </div>

      <div className="mt-4 p-3 bg-blue-50 rounded-md">
        <div className="text-sm font-medium text-blue-900 mb-2">Examples:</div>
        <ul className="text-xs text-blue-800 space-y-1">
          <li>Size surcharge: Width &gt; 1300 → Add £35 per frame</li>
          <li>Colour dep: (Anthracite or Grey) AND (Front or Back) → Add £13 to hardware</li>
          <li>Quantity: Bay Poles → Multiply £110 each</li>
          <li>Cross-category AND: (Foil Colour selected) AND (Spray = Yes) → Add £20</li>
        </ul>
      </div>
    </div>
  );
};
